import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import useSeo from "@/hooks/useSeo";
import { categories, comics, siteMeta } from "@/mocks/comics";

export default function CategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const category = categories.find((c) => c.id === id);

  const list = useMemo(
    () => (category ? comics.filter((c) => c.categoryId === category.id) : []),
    [category],
  );

  const [status, setStatus] = useState<"全部" | "连载中" | "已完结">("全部");
  const filtered = status === "全部" ? list : list.filter((c) => c.status === status);

  useSeo({
    title: category ? `${category.name}韩漫分类 - 韩漫YY榜 ${category.en}完结漫画推荐` : "分类不存在",
    description: category
      ? `韩漫YY榜${category.name}分类精选：${category.desc} 收录海量${category.name}题材韩漫，含连载中与完结作品，快速挑选你喜欢的${category.name}漫画。`
      : "该分类不存在",
    keywords: `韩漫${category?.name ?? ""},韩漫yy${category?.name ?? ""},韩漫${category?.name ?? ""}完结`,
    path: `/category/${id}`,
    structuredData: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name}韩漫分类`,
            description: category.desc,
            url: `${window.location.origin}/category/${category.id}`,
            isPartOf: { "@type": "WebSite", name: siteMeta.name, url: window.location.origin },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin },
              { "@type": "ListItem", position: 2, name: "题材分类", item: `${window.location.origin}/categories` },
              { "@type": "ListItem", position: 3, name: category.name },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <Navbar />
        <div className="text-center">
          <h1 className="font-heading text-2xl text-foreground-950">分类不存在</h1>
          <Link to="/categories" className="mt-4 inline-block rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50">
            返回分类
          </Link>
        </div>
      </div>
    );
  }

  const colorText =
    category.color === "accent"
      ? "text-accent-600"
      : category.color === "secondary"
        ? "text-secondary-600"
        : "text-primary-600";

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0 opacity-25">
            <img src={category.image} alt="" aria-hidden="true" className="h-full w-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 to-foreground-950/60"></div>
          <div className="relative z-10 mx-auto max-w-[1400px] px-4 md:px-6 py-12 md:py-16 text-background-50">
            <nav aria-label="面包屑" className="text-sm text-background-200">
              <ol className="flex flex-wrap items-center gap-2">
                <li><Link to="/" className="hover:text-accent-400">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/categories" className="hover:text-accent-400">题材分类</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li aria-current="page">{category.name}</li>
              </ol>
            </nav>
            <h1 className="mt-5 font-heading text-3xl md:text-5xl">{category.name}<span className="ml-3 text-xl text-background-200">{category.en}</span></h1>
            <p className="mt-3 max-w-xl text-sm text-background-200 md:text-base">{category.desc}</p>
            <div className={`mt-5 flex items-center gap-2 text-sm font-medium ${colorText}`}>
              <span className="rounded bg-background-50/10 px-3 py-1 backdrop-blur">收录 {list.length} 部作品</span>
              <span className="rounded bg-background-50/10 px-3 py-1 backdrop-blur">{category.count}</span>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <h2 className="font-heading text-2xl text-foreground-950">{category.name}作品</h2>
            <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
              {(["全部", "连载中", "已完结"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                    status === s ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {filtered.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-lg bg-background-100 py-16 text-center">
              <i className="ri-file-list-3-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-sm text-foreground-600">该分类下暂无对应状态的作品。</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}